// Post-call follow-up draft card (egg MCP Events / queue -> staff Gmail Drafts folder, never auto-sent)
// and verified canonical CRM mailbox status for Settings.
import type { Call } from '@bcio-voice/contracts';
import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { ExternalLink, Mail, RefreshCw, Voicemail } from 'lucide-react';
import { Icon } from '../../ui/Icon.tsx';
import { useWorkspace } from '../../state/workspace.tsx';
import { type ApiClient, ApiRequestError, type DataEnvelope, isAbortError } from '../../lib/api.ts';
import { describeError } from '../../lib/errorCopy.ts';
import { formatAbsolute } from '../../lib/format.ts';

export interface CallFollowupDraftView {
  idempotency_key: string;
  workflow_version: number;
  followup_kind: 'conversation_recap' | 'voicemail_followup';
  recipient_email: string | null;
  recipient_status: 'verified' | 'needs_recipient';
  subject: string;
  body_text: string;
  gmail_draft_id: string | null;
  gmail_message_id: string | null;
  gmail_open_url: string | null;
  synced_to_gmail: boolean;
  human_edited: boolean;
  attachments?: Array<{
    filename: string;
    mime_type: string;
    bytes: number;
    sha256: string;
    label?: string | null;
  }>;
  visual_pack_status?: 'none' | 'pending' | 'ready' | 'failed';
  created_at: string;
  updated_at: string;
}

export interface CallVisualPackView {
  status: 'pending' | 'generating' | 'ready' | 'failed';
  website_url: string;
  final_url: string;
  captured_at: string;
  concept_label: string;
  original_logo?: {
    filename: string;
    sha256: string;
    preserved_byte_for_byte: boolean;
  };
  observations: Array<{
    id: string;
    headline: string;
    detail: string;
    proposed_fix: string;
  }>;
  results_plan?: {
    objective: string;
    measurement_plan: string;
    timeframe_days: number;
  };
  preview_url: string | null;
}

export interface CallFollowupMcpDeliveryView {
  subscription_id: string;
  event_id: string;
  status: 'pending' | 'delivered' | 'failed' | 'expired';
  attempts: number;
  last_attempt_at?: string | null;
  delivered_at?: string | null;
  last_error?: string | null;
}

export interface CallFollowupMcpEventView {
  event_id: string;
  dispatched_at: string | null;
  deliveries: CallFollowupMcpDeliveryView[];
}

export interface CallFollowupView {
  eligible: boolean;
  reason: string;
  call_id: string;
  tenant_id: string;
  followup_kind: 'conversation_recap' | 'voicemail_followup' | null;
  status:
    | 'not_eligible'
    | 'needs_review'
    | 'waiting_transcript'
    | 'awaiting_dot'
    | 'pending_visual'
    | 'needs_mailbox_auth'
    | 'needs_recipient'
    | 'draft_ready'
    | 'human_edited';
  staff: {
    user_id: string;
    display_name: string;
    email: string;
    mailbox_connected: boolean;
    mailbox_email: string | null;
  };
  recipient: {
    status: 'verified' | 'needs_recipient';
    email: string | null;
    name: string | null;
    company: string | null;
    website_url?: string | null;
  };
  draft: CallFollowupDraftView | null;
  visual_pack?: CallVisualPackView | null;
  mcp_event?: CallFollowupMcpEventView | null;
  canonical?: CanonicalFollowupStatus | null;
  /** Client-side, not in the API payload: the CRM holds no follow-up for this call (canonical status 404). */
  canonical_missing?: boolean;
}

export interface MailboxStatusView {
  staff_email: string;
  mailbox_email: string | null;
  status: 'not_connected' | 'connected' | 'reconnect_required' | 'revoked';
  auth_mode: 'domain_wide_delegation' | 'oauth_user' | 'none';
  staff_login_required: boolean;
  can_compose: boolean;
  can_read: boolean;
  updated_at: string | null;
  last_error: string | null;
}

export interface CanonicalFollowupStatus {
  call_id: string;
  tenant_id: string;
  status: string;
  reason: string | null;
  draft_ready: boolean;
  action_key: string;
  staff_email: string;
  external_writer: { status: string; dispatch_accepted: boolean } | null;
  classification?: string | null;
  provider_receipt: { verified: boolean; operation_key: string; staff_email: string; provider_draft_id: string; provider_message_id: string; readback_at: string; human_modified: boolean } | null;
  content: { to: string; subject: string; body_text: string } | null;
}

export function mailboxReady(status: MailboxStatusView | null): boolean {
  return Boolean(status && status.status === 'connected' && status.mailbox_email &&
    status.mailbox_email.toLowerCase() === status.staff_email.toLowerCase() &&
    status.auth_mode !== 'none' && status.can_compose && status.can_read && !status.staff_login_required);
}

export function canonicalDraft(status: CanonicalFollowupStatus | null | undefined): CallFollowupDraftView | null {
  const receipt = status?.provider_receipt;
  if (!status || status.status !== 'draft_ready' || !status.draft_ready || !receipt ||
    receipt.verified !== true || receipt.operation_key !== `sidetone:${status.call_id}:draft` ||
    receipt.operation_key !== status.action_key || receipt.staff_email.toLowerCase() !== status.staff_email.toLowerCase() ||
    !receipt.provider_draft_id || !receipt.provider_message_id || !Number.isFinite(Date.parse(receipt.readback_at)) ||
    !status.content?.to || !status.content.subject || !status.content.body_text) return null;
  return {
    idempotency_key: status.action_key, workflow_version: 1, followup_kind: status.classification === 'confirmed_voicemail' ? 'voicemail_followup' : 'conversation_recap',
    recipient_email: status.content.to, recipient_status: 'verified', subject: status.content.subject,
    body_text: status.content.body_text, gmail_draft_id: receipt.provider_draft_id,
    gmail_message_id: receipt.provider_message_id,
    gmail_open_url: `https://mail.google.com/mail/u/${encodeURIComponent(receipt.staff_email)}/#drafts/${encodeURIComponent(receipt.provider_message_id)}`,
    synced_to_gmail: true, human_edited: receipt.human_modified,
    created_at: receipt.readback_at, updated_at: receipt.readback_at,
  };
}

export function hasDeliveredEggEvent(v: Pick<CallFollowupView, 'mcp_event'>): boolean {
  if (!v.mcp_event) return false;
  if (typeof v.mcp_event.dispatched_at === 'string' && v.mcp_event.dispatched_at.trim().length > 0) {
    return true;
  }
  return Array.isArray(v.mcp_event.deliveries) && v.mcp_event.deliveries.some(d => d.status === 'delivered');
}

export function isVerifiedGmailDraft(draft: CallFollowupDraftView | null | undefined): boolean {
  if (!draft) return false;
  return Boolean(draft.synced_to_gmail && draft.gmail_draft_id && draft.gmail_draft_id.trim().length > 0);
}

export function statusBadgeLabel(v: CallFollowupView): { text: string; tone: 'ok' | 'warn' | 'neutral' } {
  const c = v.canonical;
  if (!c) return v.canonical_missing ? { text: 'No follow-up', tone: 'neutral' } : { text: 'Follow-up status unverified', tone: 'warn' };
  if (canonicalDraft(c)) return { text: 'Draft ready in Gmail', tone: 'ok' };
  if (c.status === 'draft_ready') return { text: 'Draft receipt unverified', tone: 'warn' };
  if (['blocked', 'needs_review', 'failed'].includes(c.status)) return { text: 'Follow-up needs review', tone: 'warn' };
  if (c.status === 'awaiting_external_writer') {
    if (c.external_writer?.status === 'claimed') return { text: 'Egg preparing follow-up', tone: 'neutral' };
    if (c.external_writer?.status === 'due') return { text: 'Ready for Egg’s next batch', tone: 'neutral' };
    return { text: 'Queued for Egg', tone: 'neutral' };
  }
  if (c.external_writer?.status === 'submitted') return { text: 'Saving Gmail draft', tone: 'neutral' };
  if (['queued', 'running', 'retry'].includes(c.status)) return { text: 'Preparing follow-up', tone: 'neutral' };
  return { text: 'Follow-up gated or unavailable', tone: 'warn' };
}

export function describeFollowupQueueStatus(v: CallFollowupView): string {
  const c = v.canonical;
  if (!c) return v.canonical_missing ? 'No follow-up for this call.' : 'Follow-up status could not be verified. Check again to see whether your draft is ready.';
  if (canonicalDraft(c)) return 'Your draft is ready in Gmail. Review it before sending.';
  if (c.status === 'awaiting_external_writer') {
    if (c.external_writer?.status === 'claimed') return 'Egg is preparing your follow-up. Your draft will appear here when it’s ready.';
    if (c.external_writer?.status === 'due') return 'Ready for Egg’s next batch. Your draft will appear here when it’s ready.';
    return 'Queued for Egg’s next batch. Your draft will appear here when it’s ready.';
  }
  if (c.external_writer?.status === 'submitted') return 'Your follow-up is being saved to Gmail. It will appear here when it’s ready.';
  if (['queued', 'running', 'retry'].includes(c.status)) return 'Preparing this follow-up from the call. Your draft will appear here when it’s ready.';
  return 'This follow-up needs review before a draft can be prepared. Check the call and contact in CRM.';
}

// ---- Status polling -----------------------------------------------------------------------------
// Every check asks the CRM over a signed request, so the card polls only while an answer can still
// change, backs off while the CRM can't be verified, and never asks on its own for a call it already
// knows has no follow-up.

export const FOLLOWUP_POLL_MS = 4500;
export const FOLLOWUP_POLL_MAX_MS = 60_000;
/** Consecutive unverified checks before automatic polling stops. Check status starts over. */
export const FOLLOWUP_POLL_MAX_FAILURES = 6;
/** The CRM learns of a call from the call-ended webhook a few seconds after it ends. A 404 inside this
 * window means "not yet"; after it, "no follow-up for this call". */
export const FOLLOWUP_NOT_FOUND_GRACE_MS = 120_000;
const FINAL_CANONICAL = ['draft_ready', 'blocked', 'needs_review', 'failed'];

/** Delay before the next automatic check, or null when nothing more can come of asking:
 * 4.5 s while queued, then 4.5 s, 9 s, 18 s, 36 s, 60 s after consecutive misses, then stop. */
export function nextFollowupPollMs(v: Pick<CallFollowupView, 'canonical' | 'canonical_missing'> | null, failures: number): number | null {
  if (!v || v.canonical_missing || FINAL_CANONICAL.includes(v.canonical?.status ?? '')) return null;
  if (failures >= FOLLOWUP_POLL_MAX_FAILURES) return null;
  return Math.min(FOLLOWUP_POLL_MS * 2 ** Math.max(0, failures - 1), FOLLOWUP_POLL_MAX_MS);
}

/** True when a canonical-status error means the CRM has no follow-up for this call: a 404 once the CRM has
 * had time to learn of the call. A 404 sooner, a 503 or a network error is a miss that backs off. */
export function isFinalNoFollowup(err: unknown, endedAt: string | null | undefined, firstSeenAt: number, now = Date.now()): boolean {
  if (!(err instanceof ApiRequestError) || err.status !== 404) return false;
  const ended = Date.parse(endedAt ?? '');
  return now - (Number.isFinite(ended) ? ended : firstSeenAt) >= FOLLOWUP_NOT_FOUND_GRACE_MS;
}

const NO_FOLLOWUP_LIMIT = 500;
const noFollowupCalls = new Set<string>();
/** Calls this tab already learned have no follow-up, keyed by user and call. Remounting the card for one of
 * them does not ask the CRM again; only Check status or a disposition change does. */
export const noFollowupMemory = {
  has: (key: string): boolean => noFollowupCalls.has(key),
  add(key: string): void {
    noFollowupCalls.delete(key);
    noFollowupCalls.add(key);
    if (noFollowupCalls.size > NO_FOLLOWUP_LIMIT) noFollowupCalls.delete(noFollowupCalls.values().next().value as string);
  },
  delete: (key: string): void => { noFollowupCalls.delete(key); },
};

/** One canonical-status read for the card. A call this tab already knows has no follow-up is not asked about
 * again unless `recheck` (someone pressed Check status or changed the disposition). */
export async function readCanonicalFollowup(
  api: Pick<ApiClient, 'request'>,
  input: { key: string; callId: string; endedAt: string | null | undefined; seenAt: number; recheck: boolean; signal?: AbortSignal | undefined },
): Promise<{ canonical: CanonicalFollowupStatus | null; missing: boolean }> {
  if (!input.recheck && noFollowupMemory.has(input.key)) return { canonical: null, missing: true };
  try {
    const current = await api.request<DataEnvelope<CanonicalFollowupStatus>>(
      `/calls/${encodeURIComponent(input.callId)}/followup/canonical-status`, input.signal ? { signal: input.signal } : {},
    );
    noFollowupMemory.delete(input.key);
    return { canonical: current.data, missing: false };
  } catch (err) {
    if (isAbortError(err)) throw err;
    const missing = isFinalNoFollowup(err, input.endedAt, input.seenAt);
    if (missing) noFollowupMemory.add(input.key);
    return { canonical: null, missing };
  }
}

interface VisibilitySource {
  readonly visibilityState: string;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
  removeEventListener(type: 'visibilitychange', listener: () => void): void;
}
interface Timeouts {
  setTimeout(handler: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}
const browserTimeouts: Timeouts = {
  setTimeout: (h, ms) => globalThis.setTimeout(h, ms),
  clearTimeout: h => globalThis.clearTimeout(h as ReturnType<typeof globalThis.setTimeout>),
};

/** Runs `check` once after `delayMs` while the page is visible. Hiding the page cancels the timer; showing it
 * again checks at once. The returned function disarms everything. One timer at most. */
export function armFollowupPoll(delayMs: number, check: () => void, page: VisibilitySource, timers: Timeouts = browserTimeouts): () => void {
  let handle: unknown = null;
  let done = false;
  const clear = () => {
    if (handle !== null) timers.clearTimeout(handle);
    handle = null;
  };
  const disarm = () => {
    done = true;
    clear();
    page.removeEventListener('visibilitychange', onVisibility);
  };
  const fire = () => {
    if (done) return;
    disarm();
    check();
  };
  const onVisibility = () => {
    if (page.visibilityState !== 'visible') clear();
    else if (handle === null) fire();
  };
  page.addEventListener('visibilitychange', onVisibility);
  if (page.visibilityState === 'visible') handle = timers.setTimeout(fire, delayMs);
  return disarm;
}

export function PostCallFollowupSection({ call, compact = false }: { call: Call; compact?: boolean }) {
  const ws = useWorkspace();
  const [view, setView] = useState<CallFollowupView | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recipientInput, setRecipientInput] = useState('');
  const [editingRecipient, setEditingRecipient] = useState(false);
  const requestVersion = useRef(0);
  // Consecutive canonical checks that could not be verified; the polling effect reads it after each load.
  const canonicalMisses = useRef(0);
  // When this card first loaded the ended call; the 404 grace falls back to it if ended_at is unknown.
  const firstSeenAt = useRef<number | null>(null);

  const isActiveOrInternal = call.direction === 'internal' || (call.state !== 'ended' && call.state !== 'failed');
  const memoryKey = `${ws.userId}:${call.id}`;

  const load = useCallback(
    async (signal?: AbortSignal, recheck = false) => {
      if (isActiveOrInternal) return;
      const version = ++requestVersion.current;
      const seenAt = (firstSeenAt.current ??= Date.now());
      try {
        const res = await ws.api.request<DataEnvelope<CallFollowupView>>(
          `/calls/${encodeURIComponent(call.id)}/followup`,
          signal ? { signal } : {},
        );
        const { canonical, missing } = await readCanonicalFollowup(ws.api, {
          key: memoryKey, callId: call.id, endedAt: call.ended_at, seenAt, recheck, signal,
        });
        if (signal?.aborted || version !== requestVersion.current) return;
        canonicalMisses.current = canonical || missing ? 0 : canonicalMisses.current + 1;
        setView({ ...res.data, canonical, canonical_missing: missing, draft: canonicalDraft(canonical) });
        const recipient = res.data.recipient.email;
        if (recipient) setRecipientInput(current => current || recipient);
        setError(null);
      } catch (err) {
        if (!isAbortError(err) && version === requestVersion.current) {
          setView(null);
          setError(describeError(err, "Couldn't load post-call follow-up draft").title);
        }
      } finally {
        if (version === requestVersion.current && !signal?.aborted) setLoading(false);
      }
    },
    [ws.api, call.id, call.ended_at, memoryKey, isActiveOrInternal],
  );

  /** Someone asked: start the backoff over and ask the CRM again, even for a call it said has no follow-up. */
  const checkNow = useCallback(() => {
    canonicalMisses.current = 0;
    return load(undefined, true);
  }, [load]);

  useEffect(() => {
    if (isActiveOrInternal) return;
    const ctrl = new AbortController();
    void load(ctrl.signal);
    return () => ctrl.abort();
  }, [load, isActiveOrInternal, call.state, call.outcome, call.transcription_state]);

  useEffect(() => {
    if (isActiveOrInternal || !view) return;
    const delay = nextFollowupPollMs(view, canonicalMisses.current);
    if (delay === null) return;
    return armFollowupPoll(delay, () => void load(), document);
  }, [isActiveOrInternal, view, load]);

  if (isActiveOrInternal) return null;
  if (loading && !view) return null;
  if (!view && error) return <section className="detail-section followup-card"><h3 className="section-title">Post-call Gmail draft</h3><p className="field-error" role="alert">{error}</p><button className="btn btn-small" onClick={() => void checkNow()}>Check status</button></section>;
  if (!view) return null;

  const canMarkVoicemail =
    call.direction === 'outbound' &&
    call.state === 'ended' &&
    view.followup_kind !== 'voicemail_followup' &&
    !view.draft;

  // Hide on ineligible inbound missed calls where voicemail wasn't left by us
  if (!view.canonical && !view.eligible && !view.draft && view.status === 'not_eligible' && !canMarkVoicemail) {
    return null;
  }

  const updateDispositionOrRecipient = async (payload: {
    disposition?: 'left_voicemail' | 'spoke_to_prospect' | 'not_interested' | null;
    recipient_email?: string;
  }) => {
    setBusy(true);
    setError(null);
    try {
      const res = await ws.api.request<DataEnvelope<{ followup: CallFollowupView }>>(
        `/calls/${encodeURIComponent(call.id)}/followup/disposition`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        },
      );
      await checkNow();
      setEditingRecipient(false);
    } catch (err) {
      setError(describeError(err, "Couldn't update follow-up").title);
    } finally {
      setBusy(false);
    }
  };

  const onRecipientSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = recipientInput.trim();
    if (!trimmed) return;
    void updateDispositionOrRecipient({ recipient_email: trimmed });
  };

  const badge = statusBadgeLabel(view);
  const draft = view.draft;
  // The CRM has no follow-up for this call: one quiet line, no recipient prompts.
  const quiet = Boolean(view.canonical_missing);
  const kindLabel =
    (draft?.followup_kind ?? view.followup_kind) === 'voicemail_followup'
      ? 'Voicemail introduction draft'
      : 'Conversation recap draft';

  return (
    <section
      className={compact ? 'followup-card followup-compact' : 'detail-section followup-card'}
      aria-labelledby={`followup-title-${call.id}`}
    >
      <div className="pane-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
        <h3 id={`followup-title-${call.id}`} className="section-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Icon icon={Mail} /> <span>Post-call Gmail draft</span>
        </h3>
        <span className={`chip tone-${badge.tone}`}>{badge.text}</span>
      </div>

      {quiet && view.eligible ? null : <p className="meta" style={{ marginTop: '4px' }}>
        {view.eligible || draft ? `${kindLabel} for ${view.staff.display_name} (${view.staff.email}). Drafts only, never auto-sent.` : 'Mark left voicemail if you left a message and want egg to queue an introduction email draft.'}
      </p>}

      {draft ? (
        <div className="followup-draft-box" style={{ marginTop: '8px' }}>
          {!isVerifiedGmailDraft(draft) ? (
            <div className="notice notice-warn" style={{ marginBottom: '8px' }}>
              <p style={{ margin: 0 }}>
                <strong>Local draft placeholder only.</strong> This draft text is saved locally in Sidetone and is not in your Gmail Drafts folder yet.
              </p>
            </div>
          ) : null}
          <dl className="facts">
            <div>
              <dt>To</dt>
              <dd>{draft.recipient_email ?? view.recipient.email ?? 'Needs recipient email (add below or in Gmail)'}</dd>
            </div>
            <div>
              <dt>Subject</dt>
              <dd><strong>{draft.subject}</strong></dd>
            </div>
          </dl>
          <div className="pair-actions" style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {isVerifiedGmailDraft(draft) && draft.gmail_open_url ? (
              <a
                href={draft.gmail_open_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn"
              >
                <Icon icon={ExternalLink} /> Open draft in Gmail
              </a>
            ) : null}
            {!view.canonical ? <button
              type="button"
              className="btn btn-quiet btn-small"
              onClick={() => setEditingRecipient(v => !v)}
            >
              {draft.recipient_email || view.recipient.email ? 'Change recipient' : 'Set recipient email'}
            </button> : null}
          </div>
          {!compact ? (
            <details className="diagnostics" style={{ marginTop: '8px' }}>
              <summary>Preview draft body</summary>
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.9em', margin: '8px 0 0 0' }}>
                {draft.body_text}
              </pre>
            </details>
          ) : null}
        </div>
      ) : view.eligible || view.canonical ? (
        <div style={{ marginTop: '8px' }}>
          <p className="field-hint">
            {describeFollowupQueueStatus(view)}
          </p>
          <div className="pair-actions" style={{ marginTop: '6px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-quiet btn-small"
              disabled={busy}
              onClick={() => void checkNow()}
            >
              <Icon icon={RefreshCw} /> Check status
            </button>
            {!view.canonical && !quiet && !view.recipient.email ? (
              <button
                type="button"
                className="btn btn-quiet btn-small"
                onClick={() => setEditingRecipient(true)}
              >
                Set recipient email
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {!view.canonical && (editingRecipient || (!quiet && view.eligible && !view.recipient.email && !compact)) ? (
        <form onSubmit={onRecipientSubmit} style={{ marginTop: '8px', display: 'flex', gap: '6px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label className="field" style={{ flex: '1 1 220px', margin: 0 }}>
            <span className="field-hint">Recipient email (verified contact)</span>
            <input
              type="email"
              value={recipientInput}
              onChange={e => setRecipientInput(e.target.value)}
              placeholder="name@company.com"
              required
            />
          </label>
          <button type="submit" className="btn btn-small" disabled={busy}>
            Save recipient
          </button>
        </form>
      ) : null}

      {view.visual_pack && !compact ? (
        <div className="followup-visual-pack" style={{ marginTop: '10px', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border-subtle, #dde2ec)', background: 'var(--surface-subtle, #f8faff)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <strong style={{ fontSize: '0.88em' }}>Website Audit &amp; Proposed Concept Pack</strong>
            <span className="chip tone-neutral" style={{ fontSize: '0.75em' }}>
              {view.visual_pack.concept_label || 'Proposed Concept Direction, Original Logo Retained'}
            </span>
          </div>
          {view.visual_pack.observations.length > 0 ? (
            <ul style={{ margin: '8px 0 0 18px', padding: 0, fontSize: '0.86em', lineHeight: 1.45 }}>
              {view.visual_pack.observations.map(obs => (
                <li key={obs.id} style={{ marginBottom: '4px' }}>
                  <strong>{obs.headline}:</strong> {obs.detail}
                </li>
              ))}
            </ul>
          ) : null}
          {view.visual_pack.results_plan ? (
            <p className="field-hint" style={{ marginTop: '6px', fontSize: '0.84em' }}>
              <strong>30-day measurement plan:</strong> {view.visual_pack.results_plan.measurement_plan}
            </p>
          ) : null}
          {view.visual_pack.preview_url ? (
            <div style={{ marginTop: '8px' }}>
              <img
                src={view.visual_pack.preview_url}
                alt="Proposed Website Concept Preview (Not a Live Change)"
                style={{ width: '100%', maxWidth: '520px', borderRadius: '6px', border: '1px solid #d5dce8', display: 'block' }}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {!view.canonical && view.status === 'needs_review' && !draft ? (
        <div className="notice notice-warn" style={{ marginTop: '8px' }}>
          <p style={{ margin: '0 0 6px 0' }}>
            <strong>Confirm call outcome before drafting ({view.reason.replace(/_/g, ' ')}).</strong> Choose whether you spoke with the decision-maker or left a voicemail so egg queues the right follow-up draft.
          </p>
          <div className="pair-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-small"
              disabled={busy}
              onClick={() => void updateDispositionOrRecipient({ disposition: 'spoke_to_prospect' })}
            >
              Spoke to prospect — draft recap
            </button>
            <button
              type="button"
              className="btn btn-quiet btn-small"
              disabled={busy}
              onClick={() => void updateDispositionOrRecipient({ disposition: 'left_voicemail' })}
            >
              <Icon icon={Voicemail} /> Left voicemail — draft intro
            </button>
          </div>
        </div>
      ) : null}

      {!view.canonical && canMarkVoicemail && view.status !== 'needs_review' ? (
        <div style={{ marginTop: '8px' }}>
          <button
            type="button"
            className="btn btn-quiet btn-small"
            disabled={busy}
            onClick={() => void updateDispositionOrRecipient({ disposition: 'left_voicemail' })}
          >
            <Icon icon={Voicemail} /> Left voicemail — draft intro email
          </button>
        </div>
      ) : null}

      {error ? <p className="field-error" role="alert" style={{ marginTop: '6px' }}>{error}</p> : null}
    </section>
  );
}

export function MailboxStatusContent({ status, error, loading = false }: { status: MailboxStatusView | null; error?: string | null; loading?: boolean }) {
  const ready = mailboxReady(status);
  return <>
    <p className="field-hint" style={{ display: 'block', overflowWrap: 'anywhere' }}>
      Egg prepares call follow-ups every three hours. They appear in your mailbox for you to review and send.
    </p>
    {loading ? <p className="field-hint">Checking mailbox status…</p> : <dl className="facts">
      <div><dt>Status</dt><dd>{ready ? 'Ready for drafts' : status ? 'Mailbox access requires review' : 'Mailbox status unverified'}</dd></div>
      <div><dt>Provider</dt><dd>{status?.auth_mode !== 'none' && status ? 'Google Workspace / Gmail' : 'Unverified'}</dd></div>
      {status?.mailbox_email ? <div><dt>Mailbox</dt><dd style={{ overflowWrap: 'anywhere' }}>{status.mailbox_email}</dd></div> : null}
      <div><dt>Connection</dt><dd>{ready && status?.auth_mode === 'domain_wide_delegation' ? 'Managed by Brandcore' : 'Use the CRM mailbox settings for access review'}</dd></div>
      {status?.updated_at ? <div><dt>Status updated</dt><dd>{formatAbsolute(status.updated_at)}</dd></div> : null}
    </dl>}
    {ready && status?.mailbox_email ? <a className="btn" style={{ justifySelf: 'start' }} href={`https://mail.google.com/mail/u/${encodeURIComponent(status.mailbox_email)}/#drafts`} target="_blank" rel="noopener noreferrer"><Icon icon={ExternalLink} /> Open mailbox drafts</a> : null}
    {status?.last_error ? <p className="field-error" role="alert">Mailbox issue: {status.last_error}</p> : null}
    {error ? <p className="field-error" role="alert">{error}</p> : null}
  </>;
}

export function MailboxSettingsTile({ readOnly: _readOnly = false }: { readOnly?: boolean }) {
  const ws = useWorkspace();
  const [status, setStatus] = useState<MailboxStatusView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestVersion = useRef(0);
  const load = useCallback(async (signal?: AbortSignal) => {
    const version = ++requestVersion.current;
    setLoading(true);
    setStatus(null);
    try {
      const res = await ws.api.request<DataEnvelope<MailboxStatusView>>('/mailbox/canonical-status', signal ? { signal } : {});
      if (!signal?.aborted && version === requestVersion.current) { setStatus(res.data); setError(null); }
    } catch (err) {
      if (!isAbortError(err) && version === requestVersion.current) setError(describeError(err, 'Mailbox status unavailable').title);
    } finally { if (!signal?.aborted && version === requestVersion.current) setLoading(false); }
  }, [ws.api]);
  useEffect(() => { const ctrl = new AbortController(); void load(ctrl.signal); return () => ctrl.abort(); }, [load]);
  return <section className="settings-tile" aria-labelledby="set-gmail-drafts">
    <h2 id="set-gmail-drafts" className="section-title">Post-call Gmail drafts</h2>
    <MailboxStatusContent status={status} loading={loading} error={error} />
    <button className="btn btn-quiet btn-small" style={{ justifySelf: 'start' }} disabled={loading} onClick={() => void load()}><Icon icon={RefreshCw} /> Check status</button>
  </section>;
}
